import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaSave, FaPlus, FaTrash } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import PropTypes from 'prop-types';

const CreateBillModal = ({ onClose, onSubmit, vendors = [] }) => {
  const { t, i18n } = useTranslation();
  
  // Early warning on mount if onSubmit is not a function
  useEffect(() => {
    if (typeof onSubmit !== 'function') {
      console.error('[CreateBillModal] Expected onSubmit to be a function but got:', typeof onSubmit);
    }
  }, [onSubmit]);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    vendorId: '',
    billNumber: '',
    billDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    description: '',
    reference: '',
    category: '',
    notes: '',
    lineItems: [
      { description: '', quantity: 1, unitPrice: '', total: 0 }
    ],
    subtotal: 0,
    taxRate: 15,
    taxAmount: 0,
    totalAmount: 0
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Generate bill number on component mount
  useEffect(() => {
    const generateBillNumber = () => {
      const today = new Date();
      const year = today.getFullYear().toString().slice(-2);
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const timestamp = Date.now().toString().slice(-6);
      
      return `BILL-${year}${month}${day}-${timestamp}`;
    };

    setFormData(prev => ({
      ...prev,
      billNumber: generateBillNumber()
    }));
  }, []);

  // Force re-render when language changes
  useEffect(() => {
    // This will trigger a re-render when language changes
    const handleLanguageChange = () => {
      // Force a small state update to trigger re-render
      setFormData(prev => ({ ...prev }));
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const calculateTotals = (lineItems, taxRate = 15) => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxAmount = (subtotal * taxRate) / 100;
    const totalAmount = subtotal + taxAmount;
    
    return { subtotal, taxAmount, totalAmount };
  };

  const updateLineItem = (index, field, value) => {
    const newLineItems = [...formData.lineItems];
    newLineItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = parseFloat(newLineItems[index].quantity) || 0;
      const unitPrice = parseFloat(newLineItems[index].unitPrice) || 0;
      newLineItems[index].total = quantity * unitPrice;
    }
    
    const { subtotal, taxAmount, totalAmount } = calculateTotals(newLineItems, formData.taxRate);
    
    setFormData(prev => ({
      ...prev,
      lineItems: newLineItems,
      subtotal,
      taxAmount,
      totalAmount
    }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, unitPrice: '', total: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.lineItems.length > 1) {
      const newLineItems = formData.lineItems.filter((_, i) => i !== index);
      const { subtotal, taxAmount, totalAmount } = calculateTotals(newLineItems, formData.taxRate);
      
      setFormData(prev => ({
        ...prev,
        lineItems: newLineItems,
        subtotal,
        taxAmount,
        totalAmount
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.vendorId) {
      newErrors.vendorId = t('common.required_field');
    }
    
    if (!formData.billDate) {
      newErrors.billDate = t('common.required_field');
    }
    
    if (!formData.dueDate) {
      newErrors.dueDate = t('common.required_field');
    }
    
    if (formData.lineItems.some(item => !item.description)) {
      newErrors.lineItems = t('accounting.item_description_required');
    }
    
    if (formData.totalAmount <= 0) {
      newErrors.totalAmount = t('common.invalid_amount');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Guard: check if onSubmit is a function before calling
    if (typeof onSubmit !== 'function') {
      console.error('[CreateBillModal] onSubmit is not a function:', onSubmit);
      alert('Error: Unable to save bill. onSubmit function is not available.');
      return;
    }
    
    setLoading(true);
    try {
      const selectedVendor = vendors.find(v => v.id === formData.vendorId);
      const billData = {
        ...formData,
        vendorName: selectedVendor?.name || selectedVendor?.companyName,
        billDate: new Date(formData.billDate),
        dueDate: new Date(formData.dueDate),
        companyId: user.companyId,
        createdBy: user.uid
      };
      
      await onSubmit(billData);
    } catch (error) {
      console.error('Error creating bill:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Recalculate totals when tax rate changes
    if (name === 'taxRate') {
      const { subtotal, taxAmount, totalAmount } = calculateTotals(formData.lineItems, parseFloat(value) || 0);
      setFormData(prev => ({
        ...prev,
        taxRate: parseFloat(value) || 0,
        subtotal,
        taxAmount,
        totalAmount
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">{t('bill_payments.createBill')}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Bill Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('accounting.bill_info')}</h3>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('vendors.vendor_name')} *
              </label>
              <select
                name="vendorId"
                value={formData.vendorId}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.vendorId ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('common.select_option')}</option>
                {vendors.map(vendor => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name || vendor.companyName}
                  </option>
                ))}
              </select>
              {errors.vendorId && <p className="mt-1 text-sm text-red-600">{errors.vendorId}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('accounting.bill_number')}
              </label>
              <input
                type="text"
                name="billNumber"
                value={formData.billNumber}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                placeholder={t('accounting.auto_generated_bill_number')}
              />
              <p className="mt-1 text-xs text-gray-500">{t('accounting.auto_generated')}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('bill_payments.billDate')} *
              </label>
              <input
                type="date"
                name="billDate"
                value={formData.billDate}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.billDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.billDate && <p className="mt-1 text-sm text-red-600">{errors.billDate}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('accounting.due_date')} *
              </label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.dueDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.dueDate && <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('accounting.bill_reference')}
              </label>
              <input
                type="text"
                name="reference"
                value={formData.reference}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('accounting.bill_reference_placeholder')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('accounting.bill_category')}
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t('common.select_option')}</option>
                <option value="office_supplies">{t('accounting.office_supplies')}</option>
                <option value="utilities">{t('accounting.utilities')}</option>
                <option value="rent">{t('accounting.rent')}</option>
                <option value="professional_services">{t('accounting.professional_services')}</option>
                <option value="equipment">{t('accounting.equipment')}</option>
                <option value="maintenance">{t('accounting.maintenance')}</option>
                <option value="other">{t('common.other')}</option>
              </select>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('accounting.bill_description')}
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('accounting.bill_description_placeholder')}
              />
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('accounting.line_items')}</h3>
              <button
                type="button"
                onClick={addLineItem}
                className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
              >
                <FaPlus className="w-3 h-3" />
                {t('accounting.add_line_item')}
              </button>
            </div>
            
            {/* Column Headers */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3 bg-gray-50 border rounded-lg font-medium text-sm text-gray-700 mb-2">
              <div className="md:col-span-5">{t('accounting.item_description')}</div>
              <div className="md:col-span-2">{t('accounting.item_quantity')}</div>
              <div className="md:col-span-2">{t('accounting.item_unit_price')}</div>
              <div className="md:col-span-2">{t('accounting.item_total')}</div>
              <div className="md:col-span-1">{t('common.actions')}</div>
            </div>
            
            <div className="space-y-3">
              {formData.lineItems.map((item, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3 border rounded-lg">
                  <div className="md:col-span-5">
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('accounting.item_description_placeholder')}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <input
                      type="number"
                      value={item.total.toFixed(2)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <button
                      type="button"
                      onClick={() => removeLineItem(index)}
                      className="w-full px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                      disabled={formData.lineItems.length === 1}
                      title={t('accounting.remove_item')}
                    >
                      <FaTrash className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {errors.lineItems && <p className="mt-2 text-sm text-red-600">{errors.lineItems}</p>}
          </div>

          {/* Totals */}
          <div className="border-t pt-6">
            <div className="max-w-md ml-auto space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t('accounting.bill_subtotal')}:</span>
                <span>{formData.subtotal.toFixed(2)} {t('common.currency')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>{t('accounting.bill_tax_rate')} ({formData.taxRate}%):</span>
                <span>{formData.taxAmount.toFixed(2)} {t('common.currency')}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>{t('accounting.bill_total')}:</span>
                <span>{formData.totalAmount.toFixed(2)} {t('common.currency')}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('clients.notes')}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('accounting.bill_notes_placeholder')}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t('common.creating')}
                </>
              ) : (
                <>
                  <FaSave className="w-4 h-4" />
                  {t('bill_payments.createBill')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// PropTypes validation
CreateBillModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  vendors: PropTypes.array
};

export default CreateBillModal; 